import { useState, useMemo, useCallback } from 'react';
import { useSupabaseAppointments } from '../../../hooks/useSupabaseAppointments';
import { dayNames, statusLabels } from '../../../mocks/appointments';
import type { Appointment, AppointmentType } from '../../../mocks/appointments';
import RevenueSummary from './RevenueSummary';
import HourlyOccupancy from './HourlyOccupancy';
import UpcomingOverview from './UpcomingOverview';
import AdminDaySlotView from './AdminDaySlotView';
import AvailabilityManager from './bookings/AvailabilityManager';
import BlockedDatesManager from './bookings/BlockedDatesManager';
import AppointmentTypesManager from './bookings/AppointmentTypesManager';
import { timeToMinutes } from '../../../utils/occupancy';

type ViewMode = 'list' | 'calendar' | 'week';
type SubTab = 'appointments' | 'availability' | 'blocked' | 'types' | 'occupancy' | 'revenue' | 'upcoming';

function isSameDay(d1: Date, d2: Date) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function formatDateISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export default function AdminBookings() {
  const {
    appointments, types, slots, blockedDates, loading, error,
    updateAppointment, deleteAppointment, addSlot, updateSlot, deleteSlot,
    addBlockedDate, removeBlockedDate, addType, updateType, deleteType,
  } = useSupabaseAppointments();

  const [subTab, setSubTab] = useState<SubTab>('appointments');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [editingAppt, setEditingAppt] = useState<Appointment | null>(null);
  const [showDelete, setShowDelete] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  });

  const filtered = useMemo(() => {
    let list = [...appointments];
    if (statusFilter !== 'all') {
      list = list.filter((a) => a.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.email.toLowerCase().includes(q) ||
          a.service_type.toLowerCase().includes(q)
      );
    }
    return list;
  }, [appointments, statusFilter, search]);

  const handleStatusChange = async (id: string, status: string) => {
    await updateAppointment(id, { status });
    setEditingAppt(null);
  };

  const handleSaveNote = async (id: string) => {
    await updateAppointment(id, { admin_notes: adminNote });
    setEditingAppt(null);
    setAdminNote('');
  };

  const daysInMonth = useMemo(() => {
    return new Date(currentYear, currentMonth + 1, 0).getDate();
  }, [currentMonth, currentYear]);

  const firstDayOfMonth = useMemo(() => {
    return new Date(currentYear, currentMonth, 1).getDay();
  }, [currentMonth, currentYear]);

  const calendarDays = useMemo(() => {
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentYear, currentMonth, i));
    }
    return days;
  }, [firstDayOfMonth, daysInMonth, currentMonth, currentYear]);

  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  const weekAppointments = useMemo(() => {
    const result: Record<string, Appointment[]> = {};
    weekDays.forEach((d) => {
      const iso = formatDateISO(d);
      result[iso] = filtered.filter((a) => a.preferred_date === iso);
    });
    return result;
  }, [weekDays, filtered]);

  const getDayFranjaSummary = useCallback((date: Date) => {
    const iso = formatDateISO(date);
    const dow = date.getDay();
    const daySlots = slots.filter((s) => s.day_of_week === dow && s.is_active);
    const dayAppts = appointments.filter((a) => a.preferred_date === iso && a.status !== 'cancelled');

    let totalFranjas = 0;
    let totalBooked = 0;
    daySlots.forEach((slot) => {
      totalFranjas += slot.max_bookings;
      dayAppts.forEach((appt) => {
        if (!appt.preferred_time) return;
        const type = types.find((t) => t.name === appt.service_type);
        const duration = type?.duration_minutes || 30;
        const apptStart = timeToMinutes(appt.preferred_time);
        const apptEnd = apptStart + duration;
        const slotStart = timeToMinutes(slot.start_time);
        const slotEnd = timeToMinutes(slot.end_time);
        if (apptStart < slotEnd && apptEnd > slotStart) {
          totalBooked += 1;
        }
      });
    });
    return { totalFranjas, totalBooked, available: Math.max(0, totalFranjas - totalBooked) };
  }, [slots, appointments, types]);

  const goPrevWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  };

  const goNextWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  const goPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const formatPrice = (p?: number) => {
    if (p === undefined || p === null || p === 0) return null;
    return `€${p.toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center gap-2 text-brand-gray-500 text-sm">
          <i className="ri-loader-4-line animate-spin" />
          Sincronizando...
        </div>
      )}
      {error && (
        <div className="bg-red-50 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
          <i className="ri-error-warning-line" />
          {error}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {[
          { key: 'appointments' as SubTab, label: 'Citas', icon: 'ri-calendar-line' },
          { key: 'availability' as SubTab, label: 'Horarios', icon: 'ri-time-line' },
          { key: 'blocked' as SubTab, label: 'Bloqueos', icon: 'ri-lock-line' },
          { key: 'types' as SubTab, label: 'Servicios', icon: 'ri-scissors-line' },
          { key: 'occupancy' as SubTab, label: 'Ocupacion', icon: 'ri-bar-chart-grouped-line' },
          { key: 'revenue' as SubTab, label: 'Ingresos', icon: 'ri-bar-chart-line' },
          { key: 'upcoming' as SubTab, label: 'Proximos dias', icon: 'ri-dashboard-3-line' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-sans text-sm whitespace-nowrap transition-colors ${
              subTab === t.key
                ? 'bg-brand-black text-white'
                : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
            }`}
          >
            <i className={t.icon} />
            {t.label}
          </button>
        ))}
      </div>

      {/* APPOINTMENTS SECTION */}
      {subTab === 'appointments' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="font-serif text-brand-black text-2xl font-medium">
              Citas <span className="text-brand-gray-400">({filtered.length})</span>
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center bg-brand-gray-100 rounded-full p-1">
                {[
                  { key: 'list' as ViewMode, icon: 'ri-list-unordered', label: 'Lista' },
                  { key: 'calendar' as ViewMode, icon: 'ri-calendar-line', label: 'Calendario' },
                  { key: 'week' as ViewMode, icon: 'ri-layout-grid-line', label: 'Semana' },
                ].map((v) => (
                  <button
                    key={v.key}
                    onClick={() => { setViewMode(v.key); setSelectedDate(null); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-xs transition-colors whitespace-nowrap ${
                      viewMode === v.key
                        ? 'bg-white text-brand-black'
                        : 'text-brand-gray-500 hover:text-brand-black'
                    }`}
                  >
                    <i className={v.icon} />
                    {v.label}
                  </button>
                ))}
              </div>

              <input
                type="text"
                placeholder="Buscar cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:w-56 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              >
                <option value="all">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="confirmed">Confirmada</option>
                <option value="completed">Completada</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
          </div>

          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-gray-50">
                    <tr>
                      <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Cliente</th>
                      <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Servicio</th>
                      <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Fecha / Hora</th>
                      <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Estado</th>
                      <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Pago</th>
                      <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-gray-200">
                    {filtered.map((appt) => {
                      const st = statusLabels[appt.status];
                      const type = types.find((t) => t.name === appt.service_type);
                      return (
                        <tr key={appt.id} className="hover:bg-brand-gray-50/50 transition-colors">
                          <td className="px-5 py-4">
                            <p className="font-sans text-brand-black text-sm font-medium">{appt.name}</p>
                            <p className="font-sans text-brand-gray-400 text-xs">{appt.email}</p>
                            {appt.phone && <p className="font-sans text-brand-gray-400 text-xs">{appt.phone}</p>}
                          </td>
                          <td className="px-5 py-4">
                            <p className="font-sans text-brand-gray-600 text-sm">{appt.service_type}</p>
                            {type?.price !== undefined && type.price > 0 && (
                              <p className="font-sans text-brand-black text-xs font-medium mt-1">{formatPrice(type.price)}</p>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <p className="font-sans text-brand-black text-sm">{appt.preferred_date}</p>
                            <p className="font-sans text-brand-gray-400 text-xs">{appt.preferred_time}</p>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${st?.bg} ${st?.color}`}>
                              {st?.label}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            {type?.price !== undefined && type.price > 0 ? (
                              <div className="flex items-center gap-2">
                                {appt.paid ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-emerald-50 text-emerald-700">
                                    <i className="ri-checkbox-circle-line" />Pagada online
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-amber-50 text-amber-700">
                                    <i className="ri-time-line" />Pendiente
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-brand-gray-100 text-brand-gray-500">Gratuita</span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => { setEditingAppt(appt); setAdminNote(appt.admin_notes || ''); }} className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors" title="Editar"><i className="ri-pencil-line" /></button>
                              <button onClick={() => setShowDelete(appt.id)} className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors" title="Eliminar"><i className="ri-delete-bin-line" /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filtered.length === 0 && (
                <div className="p-10 text-center">
                  <p className="font-sans text-brand-gray-400 text-sm">No hay citas que coincidan</p>
                </div>
              )}
            </div>
          )}

          {/* CALENDAR VIEW */}
          {viewMode === 'calendar' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-white border border-brand-gray-200 rounded-2xl p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <button onClick={goPrevMonth} className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"><i className="ri-arrow-left-s-line text-lg" /></button>
                    <h3 className="font-serif text-brand-black text-xl font-medium">{monthNames[currentMonth]} {currentYear}</h3>
                    <button onClick={goNextMonth} className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"><i className="ri-arrow-right-s-line text-lg" /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {dayNames.map((d) => (
                      <div key={d} className="text-center font-sans text-brand-gray-400 text-xs uppercase tracking-wider py-2">{d.slice(0, 3)}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((date, idx) => {
                      if (!date) return <div key={`empty-${idx}`} className="aspect-square" />;
                      const iso = formatDateISO(date);
                      const dayAppts = filtered.filter((a) => a.preferred_date === iso);
                      const blocked = blockedDates.some((b) => b.date === iso);
                      const isToday = isSameDay(date, new Date());
                      const { totalFranjas, totalBooked } = getDayFranjaSummary(date);
                      const franjaPct = totalFranjas > 0 ? Math.round((totalBooked / totalFranjas) * 100) : 0;
                      const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
                      return (
                        <div
                          key={date.toISOString()}
                          onClick={() => setSelectedDate(date)}
                          className={`aspect-square rounded-xl border transition-all p-1 flex flex-col items-start overflow-hidden cursor-pointer ${
                            isSelected ? 'border-brand-black bg-brand-gray-50' : isToday ? 'border-brand-black bg-brand-gray-50/50' : 'border-brand-gray-100 hover:border-brand-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between w-full px-1">
                            <span className={`font-sans text-xs font-medium ${isToday || isSelected ? 'text-brand-black' : 'text-brand-gray-600'}`}>{date.getDate()}</span>
                            {totalFranjas > 0 && (
                              <span className={`font-sans text-[10px] font-medium px-1 rounded ${franjaPct >= 90 ? 'bg-red-50 text-red-600' : franjaPct >= 60 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`} title={`${totalBooked} / ${totalFranjas} plazas`}>{totalBooked}/{totalFranjas}</span>
                            )}
                          </div>
                          {dayAppts.length > 0 && (
                            <div className="w-full mt-auto space-y-0.5">
                              {dayAppts.slice(0, 2).map((a) => {
                                const st = statusLabels[a.status];
                                return (
                                  <button key={a.id} onClick={(e) => { e.stopPropagation(); setEditingAppt(a); setAdminNote(a.admin_notes || ''); }} className={`w-full text-left rounded px-1 py-0.5 font-sans text-[10px] truncate ${st?.bg} ${st?.color}`} title={`${a.name} — ${a.service_type} — ${a.preferred_time}`}>
                                    {a.preferred_time} {a.name.split(' ')[0]}
                                  </button>
                                );
                              })}
                              {dayAppts.length > 2 && <p className="font-sans text-[10px] text-brand-gray-400 px-1">+{dayAppts.length - 2} mas</p>}
                            </div>
                          )}
                          {blocked && dayAppts.length === 0 && (
                            <div className="w-full mt-auto bg-red-50 rounded px-1 py-0.5"><span className="font-sans text-[10px] text-red-500">Bloqueado</span></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-brand-gray-200 flex-wrap">
                    {Object.entries(statusLabels).map(([key, st]) => (
                      <div key={key} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${st.bg}`} /><span className="font-sans text-brand-gray-500 text-xs">{st.label}</span></div>
                    ))}
                    <div className="flex items-center gap-1.5 ml-4"><span className="w-2.5 h-2.5 rounded-full bg-emerald-50 border border-emerald-200" /><span className="font-sans text-brand-gray-500 text-xs">Libre</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-50 border border-amber-200" /><span className="font-sans text-brand-gray-500 text-xs">60%+</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-50 border border-red-200" /><span className="font-sans text-brand-gray-500 text-xs">90%+</span></div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-1">
                {selectedDate ? (
                  <AdminDaySlotView
                    date={selectedDate}
                    slots={slots}
                    appointments={appointments}
                    types={types}
                    onSelectAppointment={(appt) => { setEditingAppt(appt); setAdminNote(appt.admin_notes || ''); }}
                    onClose={() => setSelectedDate(null)}
                  />
                ) : (
                  <div className="bg-white border border-brand-gray-200 rounded-2xl p-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-400 mx-auto mb-3">
                      <i className="ri-calendar-line text-xl" />
                    </div>
                    <p className="font-sans text-brand-gray-500 text-sm">Haz clic en un día del calendario para ver sus franjas y citas</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WEEK VIEW */}
          {viewMode === 'week' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-white border border-brand-gray-200 rounded-2xl p-4 md:p-6">
                  <div className="flex items-center justify-between mb-6">
                    <button onClick={goPrevWeek} className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"><i className="ri-arrow-left-s-line text-lg" /></button>
                    <h3 className="font-serif text-brand-black text-lg font-medium text-center">{weekDays[0].getDate()} de {monthNames[weekDays[0].getMonth()]} — {weekDays[6].getDate()} de {monthNames[weekDays[6].getMonth()]}</h3>
                    <button onClick={goNextWeek} className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"><i className="ri-arrow-right-s-line text-lg" /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((d) => {
                      const iso = formatDateISO(d);
                      const dayAppts = weekAppointments[iso] || [];
                      const isToday = isSameDay(d, new Date());
                      const { totalFranjas, totalBooked } = getDayFranjaSummary(d);
                      const franjaPct = totalFranjas > 0 ? Math.round((totalBooked / totalFranjas) * 100) : 0;
                      const isSelected = selectedDate ? isSameDay(d, selectedDate) : false;
                      return (
                        <div
                          key={iso}
                          onClick={() => setSelectedDate(d)}
                          className={`border rounded-xl p-2 min-h-[180px] cursor-pointer transition-all ${
                            isSelected ? 'border-brand-black bg-brand-gray-50' : isToday ? 'border-brand-black bg-brand-gray-50/50' : 'border-brand-gray-200 hover:border-brand-gray-400'
                          }`}
                        >
                          <div className="text-center mb-2">
                            <p className="font-sans text-brand-gray-400 text-[10px] uppercase">{dayNames[d.getDay()].slice(0, 3)}</p>
                            <p className={`font-sans text-lg font-medium ${isToday || isSelected ? 'text-brand-black' : 'text-brand-gray-600'}`}>{d.getDate()}</p>
                            {totalFranjas > 0 && <span className={`inline-block mt-1 font-sans text-[10px] font-medium px-1.5 py-0.5 rounded ${franjaPct >= 90 ? 'bg-red-50 text-red-600' : franjaPct >= 60 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{totalBooked}/{totalFranjas}</span>}
                          </div>
                          <div className="space-y-1.5">
                            {dayAppts.length > 0 ? dayAppts.slice(0, 3).map((a) => {
                              const st = statusLabels[a.status];
                              return (
                                <button key={a.id} onClick={(e) => { e.stopPropagation(); setEditingAppt(a); setAdminNote(a.admin_notes || ''); }} className={`w-full text-left rounded-lg px-2 py-1 transition-colors hover:opacity-80 ${st?.bg}`}>
                                  <p className="font-sans text-[11px] font-medium">{a.preferred_time} {a.name.split(' ')[0]}</p>
                                  <p className="font-sans text-[10px] text-brand-gray-500 truncate">{a.service_type}</p>
                                </button>
                              );
                            }) : <p className="font-sans text-brand-gray-300 text-xs text-center py-4">Sin citas</p>}
                            {dayAppts.length > 3 && (
                              <p className="font-sans text-[10px] text-brand-gray-400 text-center">+{dayAppts.length - 3} mas</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-brand-gray-200">
                    {Object.entries(statusLabels).map(([key, st]) => (
                      <div key={key} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${st.bg}`} /><span className="font-sans text-brand-gray-500 text-xs">{st.label}</span></div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-1">
                {selectedDate ? (
                  <AdminDaySlotView
                    date={selectedDate}
                    slots={slots}
                    appointments={appointments}
                    types={types}
                    onSelectAppointment={(appt) => { setEditingAppt(appt); setAdminNote(appt.admin_notes || ''); }}
                    onClose={() => setSelectedDate(null)}
                  />
                ) : (
                  <div className="bg-white border border-brand-gray-200 rounded-2xl p-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-400 mx-auto mb-3">
                      <i className="ri-calendar-line text-xl" />
                    </div>
                    <p className="font-sans text-brand-gray-500 text-sm">Haz clic en un día de la semana para ver sus franjas y citas</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit modal */}
          {editingAppt && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
              <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-serif text-brand-black text-xl">Editar Cita</h3>
                  <button onClick={() => setEditingAppt(null)} className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"><i className="ri-close-line text-xl" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Estado</label>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {(['pending', 'confirmed', 'completed', 'cancelled'] as const).map((s) => (
                        <button key={s} onClick={() => handleStatusChange(editingAppt.id, s)} className={`px-3 py-1.5 rounded-full font-sans text-xs uppercase tracking-wider transition-colors whitespace-nowrap ${editingAppt.status === s ? `${statusLabels[s].bg} ${statusLabels[s].color} border border-transparent` : 'bg-brand-gray-50 text-brand-gray-500 border border-brand-gray-200 hover:border-brand-gray-400'}`}>
                          {statusLabels[s].label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    const type = types.find((t) => t.name === editingAppt.service_type);
                    return type?.price !== undefined && type.price > 0 && (
                      <div>
                        <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Estado del pago</label>
                        <div className="mt-2 flex items-center gap-3">
                          {editingAppt.paid ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-sm font-medium bg-emerald-50 text-emerald-700"><i className="ri-checkbox-circle-line text-base" />Pagada online</span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-sm font-medium bg-amber-50 text-amber-700"><i className="ri-time-line text-base" />Pendiente de pago</span>
                          )}
                          <span className="font-sans text-sm text-brand-black font-medium">{formatPrice(type.price)}</span>
                        </div>
                        <p className="font-sans text-brand-gray-400 text-xs mt-1.5">Los pagos online se confirman automáticamente.</p>
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Fecha</label><p className="font-sans text-brand-black text-sm mt-1">{editingAppt.preferred_date}</p></div>
                    <div><label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Hora</label><p className="font-sans text-brand-black text-sm mt-1">{editingAppt.preferred_time}</p></div>
                  </div>
                  <div><label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Servicio</label><p className="font-sans text-brand-black text-sm mt-1">{editingAppt.service_type}</p></div>
                  <div><label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Notas del cliente</label><p className="font-sans text-brand-gray-500 text-sm mt-1">{editingAppt.notes || '—'}</p></div>
                  <div>
                    <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Notas internas (admin)</label>
                    <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={3} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" placeholder="Notas solo visibles para el equipo..." />
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button onClick={() => handleSaveNote(editingAppt.id)} className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap">Guardar notas</button>
                    <button onClick={() => setEditingAppt(null)} className="border border-brand-gray-200 text-brand-black font-sans text-sm px-5 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap">Cerrar</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete confirm */}
          {showDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-600 mx-auto mb-4"><i className="ri-delete-bin-line text-xl" /></div>
                <h3 className="font-serif text-brand-black text-lg">Eliminar cita</h3>
                <p className="font-sans text-brand-gray-500 text-sm mt-2">Esta acción no se puede deshacer.</p>
                <div className="flex items-center gap-3 mt-6">
                  <button onClick={() => setShowDelete(null)} className="flex-1 border border-brand-gray-200 text-brand-black font-sans text-sm py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap">Cancelar</button>
                  <button onClick={() => { deleteAppointment(showDelete); setShowDelete(null); }} className="flex-1 bg-red-600 text-white font-sans text-sm py-2.5 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap">Eliminar</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* AVAILABILITY SLOTS */}
      {subTab === 'availability' && (
        <AvailabilityManager slots={slots} appointments={appointments} types={types} addSlot={addSlot} updateSlot={updateSlot} deleteSlot={deleteSlot} />
      )}

      {/* BLOCKED DATES */}
      {subTab === 'blocked' && (
        <BlockedDatesManager blockedDates={blockedDates} addBlockedDate={addBlockedDate} removeBlockedDate={removeBlockedDate} />
      )}

      {/* APPOINTMENT TYPES */}
      {subTab === 'types' && (
        <AppointmentTypesManager types={types} addType={addType} updateType={updateType} deleteType={deleteType} />
      )}

      {/* REVENUE SUMMARY */}
      {subTab === 'revenue' && <RevenueSummary appointments={appointments} types={types} />}

      {/* HOURLY OCCUPANCY */}
      {subTab === 'occupancy' && <HourlyOccupancy appointments={appointments} slots={slots} types={types} blockedDates={blockedDates} />}

      {/* UPCOMING OVERVIEW */}
      {subTab === 'upcoming' && <UpcomingOverview appointments={appointments} slots={slots} blockedDates={blockedDates} types={types} />}
    </div>
  );
}
